import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs from 'dayjs';
import { auditApi, type EntityRevision, type FieldChange } from '@/api/audit';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const TYPE_COLOR: Record<EntityRevision['revisionType'], string> = {
  ADD: 'green',
  MOD: 'blue',
  DEL: 'red',
};

const TYPE_LABEL: Record<EntityRevision['revisionType'], string> = {
  ADD: 'Created',
  MOD: 'Modified',
  DEL: 'Deleted',
};

function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function AuditHistoryPage() {
  const [entityType, setEntityType] = useState<string | undefined>();
  const [entityId, setEntityId] = useState<number | null>(null);

  const types = useQuery({
    queryKey: ['audit', 'history-types'],
    queryFn: () => auditApi.historyTypes(),
  });

  const load = useMutation({
    mutationFn: () => auditApi.history(entityType!, entityId!),
    onError: (e) => {
      // Surface "not found" without a noisy AntD message — the panel below shows it.
      console.warn(extractError(e, 'Failed to load history'));
    },
  });

  const data = load.data;

  const changeColumns: TableProps<FieldChange>['columns'] = [
    { title: 'Field', dataIndex: 'field', width: 200, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
    {
      title: 'Before',
      dataIndex: 'oldValue',
      render: (v: unknown) => (
        <Typography.Text type={v === null || v === undefined ? 'secondary' : undefined}>
          {fmtValue(v)}
        </Typography.Text>
      ),
    },
    {
      title: 'After',
      dataIndex: 'newValue',
      render: (v: unknown) => (
        <Typography.Text strong type={v === null || v === undefined ? 'secondary' : undefined}>
          {fmtValue(v)}
        </Typography.Text>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Audit"
        title="Record history"
        description="Field-level change history for any audited record. Backed by Hibernate Envers — every persisted revision is captured automatically; no UI write can bypass it."
      />

      <Card>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} md={9}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Entity type
            </Typography.Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose entity type"
              loading={types.isLoading}
              value={entityType}
              onChange={(v) => {
                setEntityType(v);
                load.reset();
              }}
              options={(types.data ?? []).map((t) => ({ value: t.key, label: t.label }))}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col xs={24} md={9}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Record id
            </Typography.Text>
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Database id of the record"
              value={entityId}
              onChange={(v) => setEntityId(typeof v === 'number' ? v : null)}
              onPressEnter={() => entityType && entityId !== null && load.mutate()}
              min={1}
            />
          </Col>
          <Col xs={24} md={6}>
            <Button
              type="primary"
              block
              icon={<ReloadOutlined />}
              loading={load.isPending}
              disabled={!entityType || entityId === null}
              onClick={() => load.mutate()}
            >
              Load history
            </Button>
          </Col>
        </Row>
      </Card>

      {load.isError && (
        <Alert
          type="error"
          showIcon
          message="No history found"
          description={extractError(load.error, 'Either the record does not exist, or no revisions have been recorded yet.')}
        />
      )}

      {data && data.revisions.length === 0 && (
        <Card>
          <Empty description="No revisions recorded for this record." />
        </Card>
      )}

      {data && data.revisions.length > 0 && (
        <>
          <Card>
            <Row gutter={[16, 12]}>
              <Col xs={12} md={6}>
                <Statistic title="Revisions" value={data.revisions.length} />
              </Col>
              <Col xs={12} md={9}>
                <Statistic
                  title="First seen"
                  value={dayjs(data.revisions[data.revisions.length - 1].revisionTimestamp).format('D MMM YYYY HH:mm:ss')}
                />
              </Col>
              <Col xs={24} md={9}>
                <Statistic
                  title="Last change"
                  value={dayjs(data.revisions[0].revisionTimestamp).format('D MMM YYYY HH:mm:ss')}
                />
              </Col>
            </Row>
          </Card>

          <Collapse
            accordion
            defaultActiveKey={String(data.revisions[0].revision)}
            items={data.revisions.map((rev) => ({
              key: String(rev.revision),
              label: (
                <Space wrap>
                  <Tag color={TYPE_COLOR[rev.revisionType]}>{TYPE_LABEL[rev.revisionType]}</Tag>
                  <Typography.Text strong>rev #{rev.revision}</Typography.Text>
                  <Typography.Text type="secondary">
                    {dayjs(rev.revisionTimestamp).format('D MMM YYYY HH:mm:ss')}
                  </Typography.Text>
                  {rev.updatedByUsername && (
                    <Typography.Text type="secondary">by {rev.updatedByUsername}</Typography.Text>
                  )}
                  {rev.changes.length > 0 && (
                    <Tag>{rev.changes.length} field{rev.changes.length === 1 ? '' : 's'} changed</Tag>
                  )}
                </Space>
              ),
              children:
                rev.revisionType === 'ADD' ? (
                  <SnapshotTable snapshot={rev.snapshot} />
                ) : rev.changes.length === 0 ? (
                  <Typography.Text type="secondary">No diffable field changes (revision recorded but values unchanged).</Typography.Text>
                ) : (
                  <Table<FieldChange>
                    rowKey="field"
                    dataSource={rev.changes}
                    columns={changeColumns}
                    pagination={false}
                    size="small"
                  />
                ),
            }))}
          />
        </>
      )}
    </div>
  );
}

function SnapshotTable({ snapshot }: { snapshot: Record<string, unknown> }) {
  const rows = Object.entries(snapshot)
    .filter(([, v]) => v !== null && v !== undefined)
    .map(([field, value]) => ({ field, value }));
  return (
    <Table
      rowKey="field"
      dataSource={rows}
      pagination={false}
      size="small"
      columns={[
        { title: 'Field', dataIndex: 'field', width: 220, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
        { title: 'Value', dataIndex: 'value', render: (v: unknown) => <Typography.Text>{fmtValue(v)}</Typography.Text> },
      ]}
    />
  );
}
